import { Children, Fragment, ReactNode, isValidElement } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@material-ui/core';

import { Link } from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';

import { Breadcrumbs } from '@spotify/confidence-core-react';

import { PageDescriptor, useOptionalPageBridgeProps } from '../utils';

import styles from './PageBreadcrumbs.module.css';

type LooseRouteFunc = (params: Record<string, string | undefined>) => string;

type PageBreadcrumbRowProps = {
  actions?: ReactNode;
  children: ReactNode;
};

export const PageBreadcrumbRow = ({
  actions,
  children,
}: PageBreadcrumbRowProps) => (
  <div className={styles.root} role="group" aria-label="Page navigation">
    {children}
    {actions && <div className={styles.actions}>{actions}</div>}
  </div>
);

const rendersNothing = (node: ReactNode): boolean => {
  if (node === null || node === undefined || node === false || node === '') {
    return true;
  }
  if (isValidElement(node) && node.type === Fragment) {
    return Children.count(node.props.children) === 0;
  }
  return false;
};

const BreadcrumbLink = ({ ancestor }: { ancestor: PageDescriptor }) => {
  const params = useParams();
  const path = useRouteRef(ancestor.routeRef) as LooseRouteFunc;
  return (
    <Link to={path(params)} className={styles.breadcrumbLink}>
      {ancestor.title}
    </Link>
  );
};

export const PageBreadcrumbs = ({ actions }: { actions?: ReactNode }) => {
  const bridgeProps = useOptionalPageBridgeProps();

  if (!bridgeProps || bridgeProps.ancestors.length === 0) {
    return null;
  }

  const { ancestors, leafLabel } = bridgeProps;
  const hasLeaf = !rendersNothing(leafLabel);

  const breadcrumbs = (
    <Breadcrumbs disableMargins>
      {ancestors.map(ancestor => (
        <BreadcrumbLink key={ancestor.id} ancestor={ancestor} />
      ))}
      {hasLeaf && <Typography noWrap>{leafLabel}</Typography>}
    </Breadcrumbs>
  );

  return <PageBreadcrumbRow actions={actions}>{breadcrumbs}</PageBreadcrumbRow>;
};
